import MiniCalendar from "components/calendar/MiniCalendar";
import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { leaveAPI } from "services/leaveAPI";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import TotalSpent from "views/admin/default/components/TotalSpent";
import PieChartCard from "views/admin/default/components/PieChartCard";
import { useNavigate } from "react-router-dom";
import { IoIosNotifications } from "react-icons/io";
import dashboardAPI from "services/dashboard";
import ProfileCard from "components/card/ProfileCard";
import { columnsDataCheck, columnsDataComplex } from "./variables/columnsData";
import Widget from "components/widget/Widget";
import CheckTable from "views/admin/default/components/CheckTable";
import ComplexTable from "views/admin/default/components/ComplexTable";
import Demo from "./components/Demo";
import MonthlyLatePunchTable from "./components/MonthlyLatePunchTable";
import TermsConditionsCard from "./components/TermsConditionsCard";
import tableDataComplex from "./variables/tableDataComplex.json";
// import Permissioncard from "./components/Permissioncard";
// import Permission from "../permission";

const Dashboard = () => {
  const { latecoming } = useAuth();
  const [dismissedLatecoming, setDismissedLatecoming] = useState(false);

  const userName = localStorage.getItem("user_name") || "User";
  const isSuperAdmin =
    localStorage.getItem("is_super_admin") === "true" ||
    localStorage.getItem("is_super_admin") === true;

  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [approvedLeaveCount, setApprovedLeaveCount] = useState(0);
  const [rejectedLeaveCount, setRejectedLeaveCount] = useState(0);
  const [todayLeaveCount, setTodayLeaveCount] = useState(0);
  const [todayDate, setTodayDate] = useState("");

  const [totalPendingLeaveCount, setTotalPendingLeaveCount] = useState(0);
  const [totalApprovedLeaveCount, setTotalApprovedLeaveCount] = useState(0);
  const [totalRejectedLeaveCount, setTotalRejectedLeaveCount] = useState(0);
  const [totalAllLeaveCount, setTotalAllLeaveCount] = useState(0);

  // Permission states
  const [totalPermissions, setTotalPermissions] = useState(0);
  const [permissionApproved, setPermissionApproved] = useState(0);
  const [permissionRejected, setPermissionRejected] = useState(0);
  const [permissionPending, setPermissionPending] = useState(0);
  const [availableLeaveBalance, setAvailableLeaveBalance] = useState(0);

  const [greeting, setGreeting] = useState("");
  const navigate = useNavigate();

  // Reset dismiss state whenever a new latecoming value arrives
  useEffect(() => {
    if (latecoming) {
      setDismissedLatecoming(false);
    }
  }, [latecoming]);

  useEffect(() => {
    dashboardAPI.getDashboardStats((res) => {
      const stats = res?.data;
      setTodayDate(stats?.date);
      setTodayLeaveCount(stats?.total_received_today || 0);
      setPendingLeaveCount(stats?.pending || 0);
      setApprovedLeaveCount(stats?.approved || 0);
      setRejectedLeaveCount(stats?.rejected || 0);

      // Set permission counts if present
      setTotalPermissions(stats?.total_permissions || 0);
      setPermissionApproved(stats?.permission_approved || 0);
      setPermissionRejected(stats?.permission_rejected || 0);
      setPermissionPending(stats?.permission_pending || 0);
    });

    // Single call for monthly leave balance
    dashboardAPI.getMonthlyLeaveBalance((data) => {
      setTotalAllLeaveCount(data?.total_requests || 0);
      setTotalPendingLeaveCount(data?.pending || 0);
      setTotalApprovedLeaveCount(data?.approved || 0);
      setTotalRejectedLeaveCount(data?.rejected || 0);
    });

    dashboardAPI.getAvailableLeaveBalance?.(
      (res) => {
        let balance = 0;
        if (typeof res === "object") {
          if (typeof res.available_balance === "number") {
            balance = res.available_balance;
          } else if (typeof res.available_balance === "string") {
            balance = Number(res.available_balance) || 0;
          } else if (res.data?.available_balance !== undefined) {
            balance = Number(res.data.available_balance) || 0;
          }
        } else if (typeof res === "number") {
          balance = res;
        }
        setAvailableLeaveBalance(balance);
      },
      () => setAvailableLeaveBalance(0)
    );
  }, []);

  const morningGreetings = [
    "Very good morning!",
    "Rise and shine!",
    "Wishing you a wonderful morning",
    "Good morning! Stay positive",
    "Have a great day ahead!",
    "Morning! Hope your day starts great",
    "Good morning! Make it a fantastic day",
    "Warm morning wishes",
    "Good morning, have a productive day",
    "Good morning! Let's make it a great day",
    "Fresh morning greetings",
    "Good morning! Seize the day",
    "Morning! Wishing you success today",
    "Good morning! Stay motivated",
    "Have a bright morning and a great day",
  ];

  const afternoonGreetings = [
    "Good Afternoon! Keep going",
    "Hope your afternoon is going well",
    "Have a productive afternoon",
    "Good afternoon! Stay focused",
    "Wishing you a successful afternoon",
    "Good afternoon! Keep up the great work",
    "Have a wonderful afternoon",
    "Good afternoon! Stay energized",
    "Hope your afternoon is filled with accomplishments",
    "Good afternoon! Keep pushing forward",
    "Wishing you a fantastic afternoon",
    "Good afternoon! Stay positive and productive",
    "Have a great rest of the day!",
    "Good afternoon! Make the most of your day",
    "Good afternoon! Keep striving for success",
  ];

  const eveningGreetings = [
    "Good Evening! Relax and enjoy",
    "Good evening! Hope you had a successful day",
    "Have a peaceful evening",
    "Good evening! Take care",
    "Wishing you a restful evening",
    "Good evening! Unwind and recharge",
    "Have a great evening ahead",
    "Good evening! Hope you had a productive day",
    "Wishing you a pleasant evening",
    "Good evening! Take time to relax",
    "Have a wonderful evening",
    "Good evening! Enjoy your time off",
    "Wishing you a calm and restful evening",
    "Good evening! Reflect on the day's achievements",
    "Have a relaxing evening and a good night!",
  ];

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      let greetingsList =
        hour < 12
          ? morningGreetings
          : hour < 17
          ? afternoonGreetings
          : eveningGreetings;
      const random =
        greetingsList[Math.floor(Math.random() * greetingsList.length)];
      setGreeting(random);
    };

    updateGreeting();
    const interval = setInterval(updateGreeting, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full">
      {/* ── Greeting ── */}
      <div className="mb-3"></div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center"></div>

      {/* ── Latecoming Popup (below greeting, above Today Leaves) ── */}
      {latecoming !== null && !dismissedLatecoming && (
        <div
          className="mb-4 w-full rounded-xl border border-red-400 bg-red-50 px-4 py-3 shadow-lg dark:border-red-600 dark:bg-red-900/30"
          role="alert"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex w-full items-center gap-3">
              <span className="text-lg text-red-600 dark:text-red-400">⚠</span>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                Hey <span className="font-bold text-blue-600">{userName}</span>,
                You arrived late to the office by{" "}
                <span className="font-bold text-blue-600">{latecoming}</span>{" "}
                minutes after the official office time.
              </p>
            </div>
            <button
              onClick={() => setDismissedLatecoming(true)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-100"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Greeting ── */}
      <div className="mb-3"></div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="w-full sm:w-[30%]">
          {isSuperAdmin && (
            <h1 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl md:text-2xl">
              Today Leaves & Permissions
            </h1>
          )}
        </div>
        <div className="w-full text-left sm:w-[70%] sm:text-right">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-white sm:text-base md:text-lg">
            Hey <span className="font-bold text-blue-600">{userName}</span>,{" "}
            {greeting}
          </h2>
        </div>
      </div>

      {/* ── Today Leaves (Super Admin only) ── */}
      {isSuperAdmin && (
        <div className="mb-5 w-full">
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            {/* Today leave */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "all");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Leave");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                {todayLeaveCount}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-300">
                Total leave
              </span>
            </div>
            {/* Pending leave */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "pending");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Leave");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-yellow-400 dark:text-yellow-400">
                {pendingLeaveCount}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Pending leave
              </span>
            </div>
            {/* Approved leave */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white  p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "approved");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Leave");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-green-500 dark:text-green-300">
                {approvedLeaveCount}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Approved leave
              </span>
            </div>
            {/* Rejected leave */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white  p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "rejected");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Leave");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-red-500 dark:text-red-500">
                {rejectedLeaveCount}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Rejected leave
              </span>
            </div>
            {/* Today permission */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "all");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Permission");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-blue-700 dark:text-purple-300">
                {totalPermissions}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Total permission
              </span>
            </div>
            {/* Permission pending */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform  hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "pending");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Permission");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-yellow-500 dark:text-pink-300">
                {permissionPending}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Permission pending
              </span>
            </div>
            {/* Permission approved */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "approved");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Permission");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-green-500 dark:text-green-300">
                {permissionApproved}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Permission approved
              </span>
            </div>
            {/* Permission rejected */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-2 shadow-md transition-transform hover:scale-110 dark:bg-[#23272f]"
              onClick={() => {
                localStorage.setItem("leave_status_filter", "rejected");
                localStorage.setItem("leave_date_filter", todayDate);
                localStorage.setItem("request_type", "Permission");
                localStorage.removeItem("monthly_leave_from");
                localStorage.removeItem("monthly_leave_to");
                navigate("/admin/leave-requests");
              }}
            >
              <span className="text-3xl font-bold text-red-500 dark:text-gray-300">
                {permissionRejected}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-300">
                Permission rejected
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Total Leaves ── */}
      <div className="mb-5 w-full">
        <div className="mb-3">
          <h1 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl md:text-2xl">
            Monthly Leaves
          </h1>
        </div>

        <div
          className={`mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 ${
            isSuperAdmin ? "md:grid-cols-4" : "md:grid-cols-5"
          }`}
        >
          {/* All */}
          <div
            className="h-28 w-full cursor-pointer overflow-hidden rounded-xl bg-white shadow-md transition-transform hover:scale-105 dark:border dark:border-[#2d3748] dark:bg-[#23272f] sm:h-32"
            onClick={() => {
              dashboardAPI.getMonthlyLeaveBalance((data) => {
                if (data && data.from_date && data.to_date) {
                  localStorage.setItem("monthly_leave_from", data.from_date);
                  localStorage.setItem("monthly_leave_to", data.to_date);
                  localStorage.setItem("monthly_leave_card_clicked", "true");
                }
                localStorage.setItem("leave_status_filter", "all");
                localStorage.removeItem("leave_date_filter");
                localStorage.setItem("request_type", "Leave");
                navigate("/admin/leave-requests");
              });
            }}
          >
            <div className="flex flex-col items-center justify-center p-3 py-2 sm:p-5 sm:py-3">
              <div className="text-2xl font-bold text-gray-800 dark:text-white sm:text-3xl">
                {totalAllLeaveCount}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                Total Leaves
              </div>
            </div>
            <div className="bg-blue-500 py-2 text-center text-sm font-semibold text-white sm:py-3 sm:text-base">
              All Leaves
            </div>
          </div>

          {/* Pending */}
          <div
            className="h-28 w-full cursor-pointer overflow-hidden rounded-xl bg-white shadow-md transition-transform hover:scale-105 dark:border dark:border-[#2d3748] dark:bg-[#23272f] sm:h-32"
            onClick={() => {
              dashboardAPI.getMonthlyLeaveBalance((data) => {
                if (data && data.from_date && data.to_date) {
                  localStorage.setItem("monthly_leave_from", data.from_date);
                  localStorage.setItem("monthly_leave_to", data.to_date);
                  localStorage.setItem("monthly_leave_card_clicked", "true");
                }
                localStorage.setItem("leave_status_filter", "pending");
                localStorage.removeItem("leave_date_filter");
                localStorage.setItem("request_type", "Leave");
                navigate("/admin/leave-requests");
              });
            }}
          >
            <div className="flex flex-col items-center justify-center p-3 py-2 sm:p-5 sm:py-3">
              <div className="text-2xl font-bold text-gray-800 dark:text-white sm:text-3xl">
                {totalPendingLeaveCount}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                Pending Leaves
              </div>
            </div>
            <div className="bg-yellow-500 py-2 text-center text-sm font-semibold text-white sm:py-3 sm:text-base">
              Leave Requests
            </div>
          </div>

          {/* Approved */}
          <div
            className="h-28 w-full cursor-pointer overflow-hidden rounded-xl bg-white shadow-md transition-transform hover:scale-105 dark:border dark:border-[#2d3748] dark:bg-[#23272f] sm:h-32"
            onClick={() => {
              dashboardAPI.getMonthlyLeaveBalance((data) => {
                if (data && data.from_date && data.to_date) {
                  localStorage.setItem("monthly_leave_from", data.from_date);
                  localStorage.setItem("monthly_leave_to", data.to_date);
                  localStorage.setItem("monthly_leave_card_clicked", "true");
                }
                localStorage.setItem("leave_status_filter", "approved");
                localStorage.removeItem("leave_date_filter");
                localStorage.setItem("request_type", "Leave");
                navigate("/admin/leave-requests");
              });
            }}
          >
            <div className="flex flex-col items-center justify-center p-3 py-2 sm:p-5 sm:py-3">
              <div className="text-2xl font-bold text-gray-800 dark:text-white sm:text-3xl">
                {totalApprovedLeaveCount}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                Approved Leaves
              </div>
            </div>
            <div className="bg-green-500 py-2 text-center text-sm font-semibold text-white sm:py-3 sm:text-base">
              Approved Leaves
            </div>
          </div>

          {/* Rejected */}
          <div
            className="h-28 w-full cursor-pointer overflow-hidden rounded-xl bg-white shadow-md transition-transform hover:scale-105 dark:border dark:border-[#2d3748] dark:bg-[#23272f] sm:h-32"
            onClick={() => {
              dashboardAPI.getMonthlyLeaveBalance((data) => {
                if (data && data.from_date && data.to_date) {
                  localStorage.setItem("monthly_leave_from", data.from_date);
                  localStorage.setItem("monthly_leave_to", data.to_date);
                  localStorage.setItem("monthly_leave_card_clicked", "true");
                }
                localStorage.setItem("leave_status_filter", "rejected");
                localStorage.removeItem("leave_date_filter");
                localStorage.setItem("request_type", "Leave");
                navigate("/admin/leave-requests");
              });
            }}
          >
            <div className="flex flex-col items-center justify-center p-3 py-2 sm:p-5 sm:py-3">
              <div className="text-2xl font-bold text-gray-800 dark:text-white sm:text-3xl">
                {totalRejectedLeaveCount}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                Rejected Leaves
              </div>
            </div>
            <div className="bg-red-500 py-2 text-center text-sm font-semibold text-white sm:py-3 sm:text-base">
              Rejected Leaves
            </div>
          </div>

          {/* Available (non-admin only) */}
          {!isSuperAdmin && (
            <div
              className="h-28 w-full overflow-hidden rounded-xl bg-white shadow-md dark:border dark:border-[#2d3748] dark:bg-[#23272f] sm:h-32"
              style={{ pointerEvents: "none" }}
            >
              <div className="flex flex-col items-center justify-center p-3 py-2 sm:p-5 sm:py-3">
                <div className="text-2xl font-bold text-gray-800 dark:text-white sm:text-3xl">
                  {availableLeaveBalance}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                  Available Leaves
                </div>
              </div>
              <div className="bg-blue-500 py-2 text-center text-sm font-semibold text-white sm:py-3 sm:text-base">
                Available Leaves
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Charts ── */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {isSuperAdmin ? <WeeklyRevenue /> : <Demo />}
        {isSuperAdmin ? (
          <div className="w-full">
            <CheckTable />
          </div>
        ) : (
          <MonthlyLatePunchTable />
        )}
      </div>

      {!isSuperAdmin && (
        <div className="mt-5 w-full">
          <TermsConditionsCard />
        </div>
      )}

      {/* ── Complex Table (Super Admin only) ── */}
      {isSuperAdmin && (
        <div className="mt-5 w-full">
          <ComplexTable
            columnsData={columnsDataComplex}
            tableData={tableDataComplex}
          />
          {/* <div className="w-full">
            <Permission />
          </div> */}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
